import React, { useState, useEffect } from 'react';
import { portfolioImages as fallbackImages } from '../data/mock';
import { Filter, Grid3x3, Grid2x2, Play } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const resolveMediaUrl = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

export const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [layout, setLayout] = useState('grid-3');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [portfolioImages, setPortfolioImages] = useState(fallbackImages);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/portfolio`);
        if (response.data.success && response.data.data.length > 0) {
          setPortfolioImages(response.data.data);
        }
      } catch (error) {
        // keep fallback images
      }
    };
    fetchPortfolio();
  }, []);

  const gridClass = layout === 'grid-3' 
    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
    : 'grid-cols-1 md:grid-cols-2';

  return (
    <div className="min-h-screen pt-24 pb-20 bg-white">
      {/* Header */}
      <section className="px-6 py-12">
        <div className="container mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold shimmer-heading mb-4 pb-2">
            Our Gallery
          </h1>
          <p className="text-xl text-gray-700">
            Explore the vibrant creations from our sessions
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 py-8 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-pink-50">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-600" />
              <button 
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'all' 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                All
              </button>
              <button 
                onClick={() => setFilter('recent')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'recent' 
                    ? 'bg-gradient-to-r from-sky-500 to-blue-500 text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Recent
              </button>
              <button 
                onClick={() => setFilter('popular')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'popular' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Popular
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setLayout('grid-3')}
                className={`p-2 rounded-lg transition-colors ${
                  layout === 'grid-3' 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setLayout('grid-2')}
                className={`p-2 rounded-lg transition-colors ${
                  layout === 'grid-2' 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <Grid2x2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery - All Photos & Videos in One Grid */}
      <section className="px-6 py-12">
        <div className="container mx-auto">
          <div className={`grid ${gridClass} gap-6`}>
            {portfolioImages.map((item) => (
              <div 
                key={item.id}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedMedia(item)}
              >
                {item.type === 'video' ? (
                  <>
                    <video 
                      src={resolveMediaUrl(item.url)}
                      className="w-full h-80 object-cover"
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-pink-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-10 h-10 text-white fill-white" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img 
                    src={resolveMediaUrl(item.url)} 
                    alt={item.title}
                    className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-pink-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-sm mb-2">{item.description}</p>
                    <p className="text-gray-400 text-xs">
                      {new Date(item.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div className="relative max-w-6xl w-full">
            <button
              onClick={() => setSelectedMedia(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl"
            >
              ✕ Close
            </button>
            {selectedMedia.type === 'video' ? (
              <video 
                src={resolveMediaUrl(selectedMedia.url)}
                controls
                autoPlay
                className="w-full max-h-[80vh] rounded-xl"
              />
            ) : (
              <img 
                src={resolveMediaUrl(selectedMedia.url)}
                alt={selectedMedia.title}
                className="w-full max-h-[80vh] object-contain rounded-xl"
              />
            )}
            <div className="mt-4 text-center text-white">
              <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-pink-500">
                {selectedMedia.title}
              </h3>
              <p className="text-gray-300 mt-2">{selectedMedia.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
